import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  MessagePlugin
} from 'tdesign-react';
import { UserIcon, LockOnIcon, RefreshIcon } from 'tdesign-icons-react';
import { captchaApi, meApi, menuApi } from '@/admin/api';
import { adminApi, tokenStorage } from '@/user/api';
import { filterMenusByPermissions } from '@/admin/utils/menu';
import type { CaptchaVO } from '@/types/api-entity';
import { MessageProvider } from '@/admin/components/GlobalMessage';

// AdminLogin 内部组件
function AdminLoginInner() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [captchaInfo, setCaptchaInfo] = useState<CaptchaVO | null>(null);
  const [username, setUsernameValue] = useState('');
  const [password, setPasswordValue] = useState('');
  const [captchaValue, setCaptchaValue] = useState('');

  // 加载验证码
  const loadCaptcha = async () => {
    try {
      const result = await captchaApi.getCaptcha();
      if (result.code === 200) {
        setCaptchaInfo(result.data);
      }
    } catch (err) {
      MessagePlugin.error('加载验证码失败');
    }
  };

  useEffect(() => {
    // 使用 tokenStorage 检查是否已登录
    if (tokenStorage.isAdminLoggedIn()) {
      navigate('/admin');
      return;
    }
    loadCaptcha();
  }, [navigate]);

  const handleSubmit = async (values: any) => {
    if (!captchaInfo) {
      MessagePlugin.error('验证码加载失败，请刷新页面');
      return;
    }
    if (!username) {
      MessagePlugin.error('请输入用户名');
      return;
    }
    if (!password) {
      MessagePlugin.error('请输入密码');
      return;
    }
    if (!captchaValue) {
      MessagePlugin.error('请输入验证码');
      return;
    }

    setLoading(true);
    try {
      // 使用 adminApi 登录（不是 userApi）
      const result = await adminApi.login({
        username: username,
        password: password,
        captcha: captchaValue,
        captchaKey: captchaInfo.captchaKey,
      });

      if (result.code === 200) {
        MessagePlugin.success('登录成功');
        // 预加载权限菜单并缓存
        try {
          const permRes = await meApi.getPermissions();
          if (permRes.code === 200) {
            const { menus, permissions } = permRes.data || {};
            if (menus && menus.length > 0) {
              localStorage.setItem('adminMenus', JSON.stringify(menus));
            } else if (permissions && permissions.length > 0) {
              const allMenusRes = await menuApi.getEnabledTree();
              if (allMenusRes.code === 200) {
                const filtered = filterMenusByPermissions(allMenusRes.data || [], permissions);
                localStorage.setItem('adminMenus', JSON.stringify(filtered));
              }
            } else {
              localStorage.setItem('adminMenus', JSON.stringify([]));
            }
          }
        } catch {
          // 菜单预加载失败不影响登录流程
        }
        // 从 location.state 获取登录前尝试访问的路径
        const from = location.state?.from || '/admin';
        navigate(from, { replace: true });
      } else {
        MessagePlugin.error(result.message || '登录失败');
        loadCaptcha();
        setCaptchaValue('');
      }
    } catch (err) {
      MessagePlugin.error('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] to-[#16213e] flex items-center justify-center">
      <Card className="w-[400px] shadow-2xl" bordered={false}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#1c5bff] rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">数</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">管理后台</h1>
          <p className="text-gray-500 mt-2">互联网生态平台管理系统</p>
        </div>

        <Form form={form} onSubmit={handleSubmit}>
          <Form.FormItem name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input
              prefixIcon={<UserIcon />}
              onChange={(val: string) => setUsernameValue(val)}
              placeholder="请输入用户名"
              size="large"
            />
          </Form.FormItem>

          <Form.FormItem name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input
              type="password"
              prefixIcon={<LockOnIcon />}
              onChange={(val: string) => setPasswordValue(val)}
              placeholder="请输入密码"
              size="large"
            />
          </Form.FormItem>

          <div className="mb-6">
            <div className="flex gap-3">
              <Input
                value={captchaValue}
                onChange={(val: string) => setCaptchaValue(val)}
                placeholder="请输入验证码"
                size="large"
                className="flex-1"
              />
              <div
                className="w-32 h-10 bg-gray-100 rounded cursor-pointer overflow-hidden flex items-center justify-center"
                onClick={loadCaptcha}
              >
                {captchaInfo ? (
                  <img src={captchaInfo.captchaImage} alt="验证码" className="w-full h-full object-cover" />
                ) : (
                  <RefreshIcon className="text-gray-400" />
                )}
              </div>
            </div>
          </div>

          <Form.FormItem>
            <Button
              theme="primary"
              type="submit"
              size="large"
              block
              loading={loading}
            >
              登录
            </Button>
          </Form.FormItem>
        </Form>
      </Card>
    </div>
  );
}

// 导出包裹 MessageProvider 的组件
export default function AdminLogin() {
  return (
    <MessageProvider>
      <AdminLoginInner />
    </MessageProvider>
  );
}
